import AppBar from '@material-ui/core/AppBar'
import Container from '@material-ui/core/Container'
import IconButton from '@material-ui/core/IconButton'
import { createStyles, makeStyles } from '@material-ui/core/styles'
import CloseIcon from '@material-ui/icons/Close'
import MenuIcon from '@material-ui/icons/Menu'
import React, { useState } from 'react'
import useLockBodyScroll from 'react-use/lib/useLockBodyScroll'

import NavigationButton from '@talentinc/gatsby-theme-ecom/components/Buttons/NavigationButton'
import CustomComponent from '@talentinc/gatsby-theme-ecom/components/CustomComponent'
import Link from '@talentinc/gatsby-theme-ecom/components/Link'
import NavigationGroup from '@talentinc/gatsby-theme-ecom/components/NavigationGroup/'
import ScrollProgress from '@talentinc/gatsby-theme-ecom/components/Post/ScrollProgress'
import XRayWrapper from '@talentinc/gatsby-theme-ecom/components/XRay/XRayWrapper'
import { withBannerAccommodator } from '@talentinc/gatsby-theme-ecom/hocs/withBannerAccommodator'
import useBrand from '@talentinc/gatsby-theme-ecom/hooks/useBrand'
import usePageData from '@talentinc/gatsby-theme-ecom/hooks/usePageData'
import usePageMeasurements from '@talentinc/gatsby-theme-ecom/hooks/usePageMeasurements'
import BlogNavigation from './BlogNavigation'

interface Props {
  showBlogNavigation?: boolean
}

const HeaderV2: React.FC<Props> = ({ showBlogNavigation }) => {
  const classes = useStyles()
  const { headerV2 } = usePageData()
  const brand = useBrand()
  const [open, setOpen] = useState(false)
  const [expanded, setExpanded] = useState<string | false>(false)
  const {
    header: [headerRef],
  } = usePageMeasurements()

  // Lock the body scroll if the mobile nav is open.
  useLockBodyScroll(open)

  if (!headerV2 || !brand) {
    return null
  }

  const handleChange =
    (panel: string) =>
    (event: React.ChangeEvent<unknown>, newExpanded: boolean) => {
      setExpanded(newExpanded ? panel : false)
    }
  return (
    <XRayWrapper id={headerV2.contentful_id}>
      <AppBar
        className={classes.appBar}
        component="header"
        elevation={0}
        position="sticky"
        color="inherit"
        ref={headerRef}
        data-gtm-role="header"
      >
        <Container className={classes.topBar} disableGutters>
          <Container className={classes.leftColumn} disableGutters>
            <Link className={classes.logo} to="/">
              <img alt={brand.name} src={brand.logo?.file.url} width="100" />
            </Link>
            <Container className={classes.navigationLeft} disableGutters>
              {headerV2?.navigationLeftSide?.map((navGroup) => (
                <NavigationGroup
                  key={navGroup.contentful_id}
                  groupClass={classes.navigationGroup}
                  linkClass={classes.link}
                  navigationGroup={navGroup}
                  expanded={expanded}
                  handleChange={handleChange}
                />
              ))}
            </Container>
          </Container>
          <Container className={classes.navigationRight} disableGutters>
            {headerV2?.navigationRightSide?.map((navGroup) => (
              <React.Fragment key={navGroup.contentful_id}>
                {navGroup.__typename === 'ContentfulCustomComponent' &&
                  brand.phoneNumber && <CustomComponent item={navGroup} />}
                {navGroup.__typename === 'ContentfulNavigationGroup' && (
                  <Container
                    className={classes.navGroupContainer}
                    disableGutters
                  >
                    <NavigationGroup
                      linkClass={classes.link}
                      navigationGroup={navGroup}
                      expanded={expanded}
                      handleChange={handleChange}
                    />
                  </Container>
                )}
                {navGroup.__typename === 'ContentfulButton' && (
                  <NavigationButton link={navGroup} />
                )}
              </React.Fragment>
            ))}
          </Container>
          <IconButton
            aria-label={open ? 'close' : 'menu'}
            className={classes.iconButton}
            color="inherit"
            onClick={() => setOpen(!open)}
          >
            {open ? <CloseIcon /> : <MenuIcon />}
          </IconButton>
        </Container>
        {showBlogNavigation && <BlogNavigation className={classes.blogNav} />}
        {open && (
          <Container className={classes.drawer} disableGutters>
            {headerV2?.navigationRightSide?.map((navGroup) => (
              <React.Fragment key={navGroup.contentful_id}>
                {navGroup.__typename === 'ContentfulCustomComponent' &&
                  brand.phoneNumber && (
                    <CustomComponent
                      item={navGroup}
                      phoneClass={classes.drawerPhone}
                    />
                  )}
              </React.Fragment>
            ))}
            <Container className={classes.drawerLinkContainer} disableGutters>
              {showBlogNavigation && <BlogNavigation />}
              <Container disableGutters>
                {headerV2?.navigationLeftSide?.map((navGroup) => (
                  <NavigationGroup
                    accordionClass={classes.accordionFirst}
                    groupClass={classes.navigationGroup}
                    linkClass={classes.link}
                    navigationGroup={navGroup}
                    key={navGroup.contentful_id}
                    expanded={expanded}
                    handleChange={handleChange}
                  />
                ))}
              </Container>
              <Container disableGutters>
                {headerV2?.navigationRightSide?.map((navGroup) => (
                  <React.Fragment key={navGroup.contentful_id}>
                    {navGroup.__typename === 'ContentfulNavigationGroup' && (
                      <NavigationGroup
                        linkClass={classes.link}
                        navigationGroup={navGroup}
                        expanded={expanded}
                        handleChange={handleChange}
                      />
                    )}
                  </React.Fragment>
                ))}
              </Container>
            </Container>
            <Container disableGutters>
              {headerV2?.navigationRightSide?.map((navGroup) => (
                <React.Fragment key={navGroup.contentful_id}>
                  {navGroup.__typename === 'ContentfulButton' && (
                    <Container
                      className={classes.drawerButtonContainer}
                      disableGutters
                    >
                      <NavigationButton
                        className={classes.navigationButtonDrawer}
                        link={navGroup}
                      />
                    </Container>
                  )}
                </React.Fragment>
              ))}
            </Container>
          </Container>
        )}
        {!open && <ScrollProgress className={classes.ScrollProgress} />}
      </AppBar>
    </XRayWrapper>
  )
}

const useStyles = makeStyles((theme) =>
  createStyles({
    accordionFirst: {
      '& > div': {
        borderTop: 'none',
      },
    },
    appBar: {
      backgroundColor: theme.palette.background.default,

      '& a': {
        color: theme.palette.text.primary,
      },
    },
    blogNav: {
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    drawer: {
      display: 'none',
      [theme.breakpoints.down('md')]: {
        backgroundColor: theme.palette.background.default,
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'unset',
        height: 'calc(100vh - 4em)',
        paddingBottom: '.5em',
        position: 'absolute',
        top: '4em',
      },
      [theme.breakpoints.down('sm')]: {
        borderTop: 'none',
      },
    },
    drawerButtonContainer: {
      display: 'none',

      [theme.breakpoints.down('sm')]: {
        borderTop: `1px solid ${theme.palette.divider}`,
        display: 'flex',
        justifyContent: 'center',
        padding: '1.75em 6% .5em 6%',

        '& button': {
          width: '100%',
        },
      },
    },
    drawerLinkContainer: {
      height: 'calc(100% - 11em)',
      overflow: 'scroll',
    },
    drawerPhone: {
      display: 'none',
      [theme.breakpoints.down('sm')]: {
        display: 'flex',
      },
    },
    iconButton: {
      display: 'none',
      [theme.breakpoints.down('md')]: {
        display: 'unset',
        padding: '0',
      },
    },
    link: {
      fontSize: '.94rem',
    },
    logo: {
      display: 'flex',
      fontSize: '1rem',
      marginBlock: '0.8em',
      marginRight: '2em',
    },
    navGroupContainer: {
      width: 'auto',
      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    navigationButtonDrawer: {
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    navigationGroup: {
      width: '83%',
      gap: '0.5rem',
      justifyContent: 'space-evenly',

      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
        padding: '0 6% 0 6%',
        width: '100%',

        '& > *': {
          borderTop: `1px solid ${theme.palette.divider}`,
          fontSize: '1.13rem',
          margin: '0',
          padding: '1.11em 0',
        },
      },
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'row',
    },
    navigationLeft: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-evenly',
      margin: 'unset',
      width: '84%',

      [theme.breakpoints.down('md')]: {
        display: 'none',
      },
    },
    navigationRight: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      margin: 'unset',
      width: '41em',

      [theme.breakpoints.down('md')]: {
        justifyContent: 'flex-end',
        width: '100%',

        '& > *': {
          margin: '0 1em',
        },
      },
      [theme.breakpoints.down('sm')]: {
        display: 'none',
      },
    },
    topBar: {
      alignItems: 'center',
      display: 'flex',
      fontSize: '.94em',
      justifyContent: 'space-between',
      maxWidth: '100%',
      padding: '0 6% 0 6%',
      zIndex: 100,
      '@media (min-width: 1456px)': {
        padding:
          '0 calc((100% - 91rem)/2 + 5.5rem) 0 calc((100% - 91rem)/2 + 5.5rem)',
      },
    },
    ScrollProgress: {
      backgroundColor: theme.colors?.gray?.[100] ?? theme.palette.text.hint,
    },
  })
)

export default withBannerAccommodator(HeaderV2)
